import type { Candle } from "../types";

/**
 * Trade simulation — the pure logic that walks a Trade-mode trade forward
 * over a Round's Forward path and returns its exit and P&L (design spec
 * §7 Trade mode, §12 data shapes). No UI.
 *
 * Sizing is the player's to pick: a trade risks a chosen dollar amount
 * (`risk`), and the position size falls out of that and the stop distance
 * (risk / |entry - stopLoss|). A stop-out therefore always loses exactly
 * `risk`, whatever the instrument's price scale, and a target at R multiples
 * away wins that many multiples of it. This is comparable across an FX pair
 * at 1.14 and an index at 4500 as a shared "units" field never could be.
 *
 * Touch detection uses each Forward candle's high/low. Sides are direction-
 * relative: a Long stops out below entry and targets above; a Short stops out
 * above entry and targets below. If a single candle spans both stop-loss and
 * take-profit, it resolves conservatively as **stop-first** (worst case for the
 * trader). If neither is touched by the end of Forward, the trade exits at the
 * final close (open-ended timeout), sized the same as a stop or target exit.
 *
 * The simulation trusts the trade's geometry (SL/TP on the correct sides of
 * entry); validating that is the TradePanel's job (spec §13), not this pure fn.
 */

/** Default dollar risk prefilled into a fresh trade. */
export const DEFAULT_RISK = 100;

export type Trade = {
  /** +1 long, -1 short. */
  direction: 1 | -1;
  entry: number;
  stopLoss: number;
  takeProfit: number;
  /** Dollars lost if the stop is hit; sizes the whole trade. */
  risk: number;
};

export type TradeResult = {
  exit: number;
  reason: "stop" | "target" | "timeout";
  /** Number of Forward candles held until exit (1-based; 0 if Forward empty). */
  barsHeld: number;
  /** Realized P&L in dollars. */
  pnl: number;
};

/**
 * How much of the instrument `risk` dollars buys, given how far away the
 * stop-loss sits. A stop right on entry has no distance to size against, so
 * it buys nothing rather than dividing by zero.
 */
export function positionSize(trade: Pick<Trade, "entry" | "stopLoss" | "risk">): number {
  const distance = Math.abs(trade.entry - trade.stopLoss);
  if (!Number.isFinite(distance) || distance === 0) return 0;
  return trade.risk / distance;
}

function pnl(trade: Trade, exit: number): number {
  return (exit - trade.entry) * trade.direction * positionSize(trade);
}

/** Whether `candle` touches the stop-loss, given trade direction. */
function touchesStop(trade: Trade, candle: Candle): boolean {
  return trade.direction === 1
    ? candle.low <= trade.stopLoss // long: stop is below entry
    : candle.high >= trade.stopLoss; // short: stop is above entry
}

/** Whether `candle` touches the take-profit, given trade direction. */
function touchesTarget(trade: Trade, candle: Candle): boolean {
  return trade.direction === 1
    ? candle.high >= trade.takeProfit // long: target is above entry
    : candle.low <= trade.takeProfit; // short: target is below entry
}

function outcome(
  trade: Trade,
  exit: number,
  reason: TradeResult["reason"],
  barsHeld: number,
): TradeResult {
  return { exit, reason, barsHeld, pnl: pnl(trade, exit) };
}

/**
 * Simulate `trade` over `forward`, candle by candle. Returns the realized exit,
 * the reason it closed, bars held, and dollar P&L. An empty Forward exits flat
 * at entry (timeout, 0 bars, 0 P&L).
 */
export function simulateTrade(trade: Trade, forward: Candle[]): TradeResult {
  for (let i = 0; i < forward.length; i++) {
    const candle = forward[i];

    // Same-candle-both resolves as stop-first (conservative), so check stop first.
    if (touchesStop(trade, candle)) {
      return outcome(trade, trade.stopLoss, "stop", i + 1);
    }
    if (touchesTarget(trade, candle)) {
      return outcome(trade, trade.takeProfit, "target", i + 1);
    }
  }

  // Neither barrier touched: exit at the final close (or entry if no Forward).
  const exit = forward.length > 0 ? forward[forward.length - 1].close : trade.entry;
  return outcome(trade, exit, "timeout", forward.length);
}

/**
 * Average True Range over the last `period` candles, the plain arithmetic mean
 * of true ranges (not Wilder's smoothing: the panel only needs a volatility
 * yardstick for its default stop distance, and the simple mean is the one a
 * player can reason about). Falls back to whatever history exists when the
 * Context is shorter than `period`.
 */
export function atr(candles: Candle[], period = 14): number {
  if (candles.length === 0) return 0;
  const ranges: number[] = [];
  for (let i = candles.length - 1; i > 0 && ranges.length < period; i--) {
    const c = candles[i];
    const prevClose = candles[i - 1].close;
    ranges.push(
      Math.max(c.high - c.low, Math.abs(c.high - prevClose), Math.abs(c.low - prevClose)),
    );
  }
  // A single candle has no previous close: its own range is the best estimate.
  if (ranges.length === 0) {
    const only = candles[candles.length - 1];
    return only.high - only.low;
  }
  return ranges.reduce((sum, r) => sum + r, 0) / ranges.length;
}

/**
 * How many decimals to print a price at, from its order of magnitude: an index
 * at 4500 reads fine at 2, an FX pair at 1.14 needs 4. Mirrors the chart's
 * y-axis precision (chartData.pricePrecision) so the panel and the axis agree.
 */
export function priceDecimals(price: number): number {
  const size = Math.abs(price);
  if (size >= 100) return 2;
  if (size >= 10) return 3;
  if (size >= 1) return 4;
  if (size >= 0.1) return 5;
  return 6;
}

/** Round `value` to `decimals` places, returning a number (not a string). */
export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * Level distances. A level is set as a distance from entry rather
 * than as an absolute price: an entry of 0.006733 with six-decimal stop and
 * target fields asked the player to do arithmetic against an unlabeled chart,
 * where "1 ATR below" is the thing they actually mean. The distance is
 * unsigned and carries a unit; the side comes from the trade's direction, so
 * flipping Long to Short moves the prices without touching the numbers.
 *
 * Absolute prices are still what a Trade is made of (see `Trade`): the panel
 * converts on the way in, and the chart converts on the way back out of a drag.
 */
export type LevelUnit = "atr" | "pct";

/** What one arrow-key press or stepper click moves a distance by, in units. */
export const LEVEL_STEP = 0.1;

/** Distances a fresh round prefills: a 1 ATR stop and a 2 ATR target (2:1). */
export const DEFAULT_STOP_ATR = 1;
export const DEFAULT_TARGET_ATR = 2;

/**
 * Decimals a distance is printed and rounded at, per unit. An ATR is a coarse
 * yardstick, so a tenth of one says everything a player needs. A percent is
 * not: an instrument whose ATR is a fraction of a percent (the yen future's is
 * 0.42%) would round to a tenth so hard that ATR → % → ATR came back as 0.9,
 * moving the level the switch was supposed to leave alone.
 */
export function distanceDecimals(unit: LevelUnit): number {
  return unit === "atr" ? 1 : 2;
}

/** The smallest distance `unit` can express, i.e. one in its last place. */
function smallestDistance(unit: LevelUnit): number {
  return 1 / 10 ** distanceDecimals(unit);
}

/**
 * The price one unit is worth: the ATR itself, or a hundredth of the entry.
 * A flat Context (ATR 0) has no volatility to measure against, so the ATR unit
 * falls back to one percent of the price, the same fallback the old absolute
 * prefill used. Never zero, so a conversion can always divide by it.
 */
export function unitSize(entry: number, atrValue: number, unit: LevelUnit): number {
  const size = unit === "atr" && atrValue > 0 ? atrValue : Math.abs(entry) * 0.01;
  return size > 0 ? size : 1;
}

/**
 * The absolute price a level sits at. `side` is -1 for the stop and +1 for the
 * target: combined with `direction` it puts a long's stop below entry and a
 * short's above, from the same unsigned distance.
 */
export function levelPrice(
  entry: number,
  atrValue: number,
  unit: LevelUnit,
  direction: 1 | -1,
  side: 1 | -1,
  distance: number,
): number {
  const offset = direction * side * distance * unitSize(entry, atrValue, unit);
  return roundTo(entry + offset, priceDecimals(entry));
}

/** How far, in `unit`s, an absolute price sits from entry (unsigned). */
export function priceDistance(
  price: number,
  entry: number,
  atrValue: number,
  unit: LevelUnit,
): number {
  return roundTo(Math.abs(price - entry) / unitSize(entry, atrValue, unit), distanceDecimals(unit));
}

/**
 * The same price distance expressed in the other unit, so switching units
 * leaves the lines on the chart exactly where they were.
 */
export function convertDistance(
  distance: number,
  from: LevelUnit,
  to: LevelUnit,
  entry: number,
  atrValue: number,
): number {
  if (from === to) return distance;
  const converted =
    (distance * unitSize(entry, atrValue, from)) / unitSize(entry, atrValue, to);
  return roundTo(converted, distanceDecimals(to));
}

/** A distance as it reads in the panel and on a chart label, e.g. "1.0 ATR". */
export function distanceLabel(distance: number, unit: LevelUnit): string {
  const value = distance.toFixed(distanceDecimals(unit));
  return unit === "atr" ? `${value} ATR` : `${value}%`;
}

/**
 * The prefilled distances for a fresh round: a stop one ATR from entry and a
 * target two ATRs away, so the default trade is a 2:1 sized to the
 * instrument's own volatility. In percent that is whatever those work out to,
 * rounded to a tenth and never rounded away to nothing.
 */
export function defaultDistances(
  entry: number,
  atrValue: number,
  unit: LevelUnit,
): { stop: number; target: number } {
  if (unit === "atr") return { stop: DEFAULT_STOP_ATR, target: DEFAULT_TARGET_ATR };
  const inPct = (atrs: number) =>
    Math.max(smallestDistance("pct"), convertDistance(atrs, "atr", "pct", entry, atrValue));
  return { stop: inPct(DEFAULT_STOP_ATR), target: inPct(DEFAULT_TARGET_ATR) };
}
